import React, { useState, useEffect, useCallback } from "react";
import { GameSwitcher } from "../components/GameSwitcher";
import { WindowFilter } from "../components/WindowFilter";
import { LatestDrawCard } from "../components/LatestDrawCard";
import { FrequencyChart, SpecialBallChart } from "../components/FrequencyChart";
import { Heatmap } from "../components/Heatmap";
import { OverdueList, PairsList } from "../components/OverdueList";
import { HistoryTable } from "../components/HistoryTable";
import { AIPicksCard } from "../components/AIPicksCard";
import { SavedPicks } from "../components/SavedPicks";
import { BacktestCard } from "../components/BacktestCard";
import { AlertsCard } from "../components/AlertsCard";
import { getLatest, getAnalytics, getSavedPicks, GAME_THEME } from "../lib/api";
import { Ticket, Loader2 } from "lucide-react";
import { Toaster } from "../components/ui/sonner";

const SPECIAL_NAME = { mega_millions: "Mega Ball", powerball: "Powerball" };

export default function Dashboard() {
  const [game, setGame] = useState("mega_millions");
  const [window, setWindow] = useState("all");
  const [latest, setLatest] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [saved, setSaved] = useState([]);
  const [loading, setLoading] = useState(true);
  const t = GAME_THEME[game];

  const loadSaved = useCallback(async () => {
    setSaved(await getSavedPicks(game));
  }, [game]);

  const loadDashboard = useCallback(async (isActive) => {
    setLoading(true);
    try {
      const [l, a, s] = await Promise.all([
        getLatest(game),
        getAnalytics(game, window),
        getSavedPicks(game),
      ]);
      if (isActive()) {
        setLatest(l);
        setAnalytics(a);
        setSaved(s);
      }
    } finally {
      if (isActive()) setLoading(false);
    }
  }, [game, window]);

  useEffect(() => {
    let active = true;
    loadDashboard(() => active);
    return () => { active = false; };
  }, [loadDashboard]);

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-50">
      <Toaster position="top-center" theme="dark" />

      {/* Header */}
      <header className="sticky top-0 z-40 bg-zinc-950/80 backdrop-blur-xl border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl grid place-items-center border border-zinc-800"
              style={{ backgroundColor: t.color + "1a" }}>
              <Ticket size={18} style={{ color: t.color }} />
            </div>
            <div>
              <h1 className="font-heading text-base sm:text-lg leading-none tracking-tight">TrendDraw</h1>
              <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">Analyzer</span>
            </div>
          </div>
          <GameSwitcher game={game} setGame={setGame} />
        </div>
      </header>

      {/* Hero band */}
      <div className="border-b border-zinc-800 grid-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
          <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tighter mb-2">
            <span style={{ color: t.color }}>{t.name}</span> Trends
          </h2>
          <p className="text-zinc-400 max-w-2xl text-sm sm:text-base">
            Data-driven insight into number frequency, overdue gaps and pair patterns —
            plus AI-suggested sets. Draws are random; this is for entertainment.
          </p>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        {loading || !analytics ? (
          <div className="flex flex-col items-center justify-center py-32 text-zinc-500">
            <Loader2 size={28} className="animate-spin mb-3" style={{ color: t.color }} />
            <p className="text-sm">Loading {t.name} data…</p>
          </div>
        ) : (
          <>
            {/* Row 1: latest + AI picks */}
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
              <div className="lg:col-span-2"><LatestDrawCard data={latest} game={game} /></div>
              <div className="lg:col-span-3"><AIPicksCard game={game} window={window} onSaved={loadSaved} /></div>
            </div>

            {/* Window filter bar */}
            <div className="flex items-center justify-between flex-wrap gap-3 pt-2">
              <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold">
                Analytics · {analytics.total_draws.toLocaleString()} draws
              </div>
              <WindowFilter window={window} setWindow={setWindow} color={t.color} />
            </div>

            <FrequencyChart hot={analytics.hot} cold={analytics.cold} color={t.color} />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2"><Heatmap frequency={analytics.frequency} /></div>
              <SpecialBallChart data={analytics.special_frequency} color={t.color} name={SPECIAL_NAME[game]} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <OverdueList overdue={analytics.overdue} />
              <PairsList pairs={analytics.pairs} />
            </div>

            <BacktestCard game={game} />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2"><HistoryTable game={game} /></div>
              <div className="space-y-6">
                <SavedPicks picks={saved} game={game} onChange={loadSaved} />
                <AlertsCard />
              </div>
            </div>
          </>
        )}
      </main>

      <footer className="border-t border-zinc-800 mt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 text-xs text-zinc-600 flex flex-wrap justify-between gap-2">
          <span>Data: data.ny.gov · Mega Millions since 2002 · Powerball since 2010</span>
          <span className="italic">For entertainment only — lottery draws are random.</span>
        </div>
      </footer>
    </div>
  );
}
